import { Injectable } from '@angular/core'
import { BaseSignalStore } from '@core/state/base-store'
import { SuccessionFileDocumentsDto } from '@shared/models/dto/api-dtos'

/**
 * State interface for Documents store
 * Manages documents grouped by category and additional information
 */
export interface DocumentsState {
  successionFileId: number | null
  remiseAuNotaire: SuccessionFileDocumentsDto['remiseAuNotaire']
  devisOuLettreDeMission: SuccessionFileDocumentsDto['devisOuLettreDeMission']
  piecesDefunt: SuccessionFileDocumentsDto['piecesDefunt']
  piecesHeritiers: SuccessionFileDocumentsDto['piecesHeritiers']
  echangesAvecHeritiers: SuccessionFileDocumentsDto['echangesAvecHeritiers'] | null
  piecesActifsPassifs: SuccessionFileDocumentsDto['piecesActifsPassifs'] | null
  autresInformationsUtiles: SuccessionFileDocumentsDto['autresInformationsUtiles'] | null
}

/**
 * Store for managing Documents state
 * Handles data, loading, error states for documents information
 * Provides reactive selectors for components to subscribe to state changes
 */
@Injectable({ providedIn: 'root' })
export class DocumentsStore extends BaseSignalStore<DocumentsState> {
  constructor() {
    super({
      successionFileId: null,
      remiseAuNotaire: null,
      devisOuLettreDeMission: null,
      piecesDefunt: null,
      piecesHeritiers: null,
      echangesAvecHeritiers: null,
      piecesActifsPassifs: null,
      autresInformationsUtiles: null,
    })
  }

  // Computed signal selectors (reactive, use in templates with signals)
  readonly successionFileId = this.select(state => state.successionFileId)
  readonly remiseAuNotaire = this.select(state => state.remiseAuNotaire)
  readonly devisOuLettreDeMission = this.select(state => state.devisOuLettreDeMission)
  readonly piecesDefunt = this.select(state => state.piecesDefunt)
  readonly piecesHeritiers = this.select(state => state.piecesHeritiers)
  readonly echangesAvecHeritiers = this.select(state => state.echangesAvecHeritiers)
  readonly piecesActifsPassifs = this.select(state => state.piecesActifsPassifs)
  readonly autresInformationsUtiles = this.select(state => state.autresInformationsUtiles)
  readonly hasDocuments = this.select(state => {
    const remise = state.remiseAuNotaire?.length ?? 0
    const devis = state.devisOuLettreDeMission?.length ?? 0
    const defunt = state.piecesDefunt?.length ?? 0
    const heritiers = state.piecesHeritiers?.length ?? 0
    return remise + devis + defunt + heritiers > 0
  })

  // Observable selectors for RxJS integration (use in components with async pipe)
  readonly successionFileId$ = this.select$(state => state.successionFileId)
  readonly remiseAuNotaire$ = this.select$(state => state.remiseAuNotaire)
  readonly devisOuLettreDeMission$ = this.select$(state => state.devisOuLettreDeMission)
  readonly piecesDefunt$ = this.select$(state => state.piecesDefunt)
  readonly piecesHeritiers$ = this.select$(state => state.piecesHeritiers)
  readonly echangesAvecHeritiers$ = this.select$(state => state.echangesAvecHeritiers)
  readonly piecesActifsPassifs$ = this.select$(state => state.piecesActifsPassifs)
  readonly autresInformationsUtiles$ = this.select$(state => state.autresInformationsUtiles)
  readonly hasDocuments$ = this.select$(state => {
    const remise = state.remiseAuNotaire?.length ?? 0
    const devis = state.devisOuLettreDeMission?.length ?? 0
    const defunt = state.piecesDefunt?.length ?? 0
    const heritiers = state.piecesHeritiers?.length ?? 0
    return remise + devis + defunt + heritiers > 0
  })

  /**
   * Sets all documents data from API response
   * @param data SuccessionFileDocumentsDto from API
   */
  setDocuments(data: SuccessionFileDocumentsDto): void {
    this.setState({
      successionFileId: data.successionFileId,
      remiseAuNotaire: data.remiseAuNotaire,
      devisOuLettreDeMission: data.devisOuLettreDeMission,
      piecesDefunt: data.piecesDefunt,
      piecesHeritiers: data.piecesHeritiers,
      echangesAvecHeritiers: data.echangesAvecHeritiers ?? null,
      piecesActifsPassifs: data.piecesActifsPassifs ?? null,
      autresInformationsUtiles: data.autresInformationsUtiles,
    })
  }

  /**
   * Resets the store to initial state
   * Clears all documents data
   */
  override reset(): void {
    super.reset()
  }
}
